export interface ProjectileInit {
  x: number;
  y: number;
  tx: number;
  ty: number;
  damage: number;
  colour: number;
  speed: number;
  splash: number;
  kind: 'bullet' | 'shell' | 'flame' | 'beam' | 'missile' | 'arc' | 'plasma' | 'cluster' | 'frost' | 'enemy';
  burnDps?: number;
  slowPct?: number;
  armourPen?: number;
  critChance?: number;
  damageType: 'BALLISTIC' | 'THERMAL' | 'EXPLOSIVE' | 'ENERGY';
  sourceId: string;
  trail?: boolean;
  /** Guided projectile that steers toward `targetId`. */
  homing?: boolean;
  targetId?: number;
  /** Optional hard travel limit used by short range flame projectiles. */
  maxTravel?: number;
}

import { TrailHistory } from '../sim/Vfx';
import type { Enemy } from './Enemy';

export class Projectile {
  x = 0;
  y = 0;
  tx = 0;
  ty = 0;
  vx = 0;
  vy = 0;
  speed = 0;
  damage = 0;
  colour = 0xffffff;
  splash = 0;
  kind: ProjectileInit['kind'] = 'bullet';
  burnDps = 0;
  slowPct = 0;
  armourPen = 0;
  critChance = 0;
  damageType: ProjectileInit['damageType'] = 'BALLISTIC';
  sourceId = '';
  life = 2.5;
  alive = false;
  trail = true;
  angle = 0;
  homing = false;
  targetId: number | null = null;
  history = new TrailHistory();
  age = 0;
  private travelled = 0;
  private maxTravel = 0;

  spawn(init: ProjectileInit): void {
    this.x = init.x;
    this.y = init.y;
    this.tx = init.tx;
    this.ty = init.ty;
    const dx = init.tx - init.x;
    const dy = init.ty - init.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.speed = init.speed;
    this.vx = (dx / dist) * init.speed;
    this.vy = (dy / dist) * init.speed;
    this.angle = Math.atan2(dy, dx);
    this.damage = init.damage;
    this.colour = init.colour;
    this.splash = init.splash;
    this.kind = init.kind;
    this.burnDps = init.burnDps ?? 0;
    this.slowPct = init.slowPct ?? 0;
    this.armourPen = init.armourPen ?? 0;
    this.critChance = init.critChance ?? 0;
    this.damageType = init.damageType;
    this.sourceId = init.sourceId;
    this.trail = init.trail ?? true;
    this.homing = init.homing ?? false;
    this.targetId = init.targetId ?? null;
    this.travelled = 0;
    this.maxTravel = Math.max(0, init.maxTravel ?? 0);
    this.life = 2.5;
    this.alive = true;
    this.age = 0;
    this.history.reset();
  }

  update(dt: number, enemies?: Enemy[]): boolean {
    if (!this.alive) return false;
    if (this.homing && this.targetId != null && enemies) {
      const target = enemies.find((e) => e.id === this.targetId && e.alive);
      if (target) {
        const dx = target.x - this.x;
        const dy = target.y - this.y;
        const d = Math.hypot(dx, dy) || 1;
        const desiredX = (dx / d) * this.speed;
        const desiredY = (dy / d) * this.speed;
        const steer = Math.min(1, dt * 6);
        this.vx += (desiredX - this.vx) * steer;
        this.vy += (desiredY - this.vy) * steer;
        this.angle = Math.atan2(this.vy, this.vx);
      }
    }
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.travelled += Math.hypot(this.vx * dt, this.vy * dt);
    this.life -= dt;
    this.age += dt;
    this.history.push(this.x, this.y, dt);
    this.history.tick(dt);
    if (this.life <= 0) this.alive = false;
    if (this.maxTravel > 0 && this.travelled >= this.maxTravel) this.alive = false;
    return this.alive;
  }
}

export class ProjectilePool {
  private pool: Projectile[] = [];
  private active: Projectile[] = [];

  constructor(size = 128) {
    for (let i = 0; i < size; i++) this.pool.push(new Projectile());
  }

  fire(init: ProjectileInit): Projectile {
    let p = this.pool.pop();
    if (!p) {
      p = new Projectile();
    }
    p.spawn(init);
    this.active.push(p);
    return p;
  }

  getActive(): Projectile[] {
    return this.active;
  }

  update(dt: number, enemies?: Enemy[]): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      if (!p.update(dt, enemies)) {
        this.active.splice(i, 1);
        p.alive = false;
        this.pool.push(p);
      }
    }
  }

  recycle(p: Projectile): void {
    p.alive = false;
    const idx = this.active.indexOf(p);
    if (idx >= 0) this.active.splice(idx, 1);
    this.pool.push(p);
  }

  clear(): void {
    for (const p of this.active) this.pool.push(p);
    this.active.length = 0;
  }
}
